import React from "react";
import AsyncSelect from "react-select/async";
import { buildSelectStyles } from "../utils/selectStyles";

/**
 * DataMappingMultiSelect Component
 *
 * A reusable multi-select dropdown component using react-select, for the
 * Data Mapping page - sibling of DataMappingSingleSelect, same
 * loadOptions/selectedValues/onChange(values: string[]) contract and
 * styling, just with isMulti=true and array-shaped value/onChange instead
 * of taking only the first selection.
 *
 * Props:
 * - loadOptions: Function to load options asynchronously
 * - selectedValues: Array of selected values
 * - onChange: Function to handle selection changes (values: string[]) => void
 * - placeholder: Optional placeholder text
 * - isDisabled: Optional boolean to disable the dropdown
 * - className: Optional CSS class name
 */
const DataMappingMultiSelect = ({
  loadOptions = [],
  selectedValues = [],
  onChange,
  placeholder = "Select options...",
  isDisabled = false,
  className = "",
}) => {
  const loadingMessage = "Loading Tables...";

  const validSelectedValues = Array.isArray(selectedValues)
    ? selectedValues.filter(
        value => value && typeof value === "string" && value.trim() !== ""
      )
    : [];

  const selectedOptions = validSelectedValues.map(value => ({
    value,
    label: value,
  }));

  const handleChange = selectedOptions => {
    if (!selectedOptions) {
      onChange([]);
      return;
    }
    onChange(selectedOptions.map(option => option.value));
  };

  // Shared react-select styling — see utils/selectStyles.js.
  const customStyles = buildSelectStyles({ isDisabled });

  return (
    <div className={`data-mapping-multiselect-container ${className}`}>
      <AsyncSelect
        isMulti={true}
        loadOptions={async inputValue => {
          if (typeof loadOptions !== "function") {
            console.error(
              "DataMappingMultiSelect - loadOptions is not a function:",
              loadOptions
            );
            return [];
          }
          try {
            const result = await loadOptions(inputValue);
            return result;
          } catch (error) {
            console.error("DataMappingMultiSelect - loadOptions error:", error);
            return [];
          }
        }}
        value={selectedOptions}
        onChange={handleChange}
        styles={customStyles}
        placeholder={placeholder}
        isClearable={true}
        isSearchable={true}
        isDisabled={isDisabled}
        className={className || "data-mapping-multiselect"}
        classNamePrefix="data-mapping-multiselect"
        noOptionsMessage={() => "No options available"}
        loadingMessage={() => loadingMessage}
        cacheOptions={true}
        defaultOptions={true}
      />
    </div>
  );
};

export default DataMappingMultiSelect;
